import { Pause, Play, RotateCcw, SkipBack, SkipForward } from "lucide-react";
import { TOTAL_PLAYBACK_MS, stageStartTimes, type SneezeStage } from "../content/sneeze";
import { formatTimecode } from "../lib/format";

interface TimelineProps {
  elapsedMs: number;
  isPlaying: boolean;
  speed: number;
  stages: SneezeStage[];
  activeIndex: number;
  exploring: boolean;
  onTogglePlaying: () => void;
  onSeek: (timeMs: number) => void;
  onPrevious: () => void;
  onNext: () => void;
  onSpeedChange: (speed: number) => void;
  onResetCamera: () => void;
}

export function Timeline({
  elapsedMs,
  isPlaying,
  speed,
  stages,
  activeIndex,
  exploring,
  onTogglePlaying,
  onSeek,
  onPrevious,
  onNext,
  onSpeedChange,
  onResetCamera,
}: TimelineProps) {
  const progress = elapsedMs / TOTAL_PLAYBACK_MS;
  const activeStage = stages[activeIndex];

  return (
    <section className="timeline panel" aria-label="Guided sequence controls">
      <div className="timeline__head">
        <div className="transport">
          <button type="button" className="icon-button icon-button--quiet" onClick={onPrevious} aria-label="Previous stage">
            <SkipBack size={15} fill="currentColor" />
          </button>
          <button type="button" className="play-button" onClick={onTogglePlaying} aria-label={isPlaying ? "Pause" : "Play"}>
            {isPlaying ? <Pause size={17} fill="currentColor" /> : <Play size={17} fill="currentColor" />}
          </button>
          <button type="button" className="icon-button icon-button--quiet" onClick={onNext} aria-label="Next stage">
            <SkipForward size={15} fill="currentColor" />
          </button>
          <div className="timeline__title">
            <strong>{activeStage.navLabel}</strong>
            <span>{exploring ? "Free exploration · playback paused" : activeStage.physiologicTiming}</span>
          </div>
        </div>

        <div className="timeline__tools">
          {exploring && (
            <button type="button" className="reset-view" onClick={onResetCamera}>
              <RotateCcw size={12} /> Return to guided view
            </button>
          )}
          <label className="speed-control">
            <span>Speed</span>
            <select value={speed} onChange={(event) => onSpeedChange(Number(event.currentTarget.value))}>
              <option value={0.5}>0.5×</option>
              <option value={1}>1×</option>
              <option value={1.5}>1.5×</option>
              <option value={2}>2×</option>
            </select>
          </label>
          <time className="timecode">{formatTimecode(elapsedMs)}</time>
        </div>
      </div>

      <div className="timeline__track">
        <div className="timeline__fill" style={{ transform: `scaleX(${progress})` }} aria-hidden="true" />
        {stageStartTimes.slice(1).map((time) => (
          <span
            key={time}
            className="timeline__tick"
            style={{ left: `${(time / TOTAL_PLAYBACK_MS) * 100}%` }}
            aria-hidden="true"
          />
        ))}
        <input
          type="range"
          min="0"
          max={TOTAL_PLAYBACK_MS}
          step="10"
          value={Math.min(elapsedMs, TOTAL_PLAYBACK_MS)}
          onChange={(event) => onSeek(Number(event.currentTarget.value))}
          aria-label="Timeline position"
        />
      </div>

      <div className="timeline__labels" aria-hidden="true">
        {stages.map((stage, index) => (
          <span className={index === activeIndex ? "is-active" : ""} key={stage.id}>
            {stage.navLabel}
          </span>
        ))}
      </div>
    </section>
  );
}
